import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import {
  cancelEmailRemMeeting,
  createEmailRemAlias,
  deleteEmailRemAlias,
  getEmailRemStatus,
  listEmailRemAliases,
  listEmailRemMeetings,
  listEmailRemTargets,
  regenerateEmailRemAlias,
  updateEmailRemAlias,
} from '../../services/emailRem/emailRemApi';
import { isEmailRemEnabledForUser } from '../../utils/emailRemEntitlement';

const OFFSETS = [5, 10, 15, 30, 60];

const sectionSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  p: { xs: 1.25, sm: 1.5 },
};

function emptyForm() {
  return { label: '', targetType: 'flow', targetId: '', offsetMinutes: 15 };
}

function formatWhen(iso, zone) {
  if (!iso) return '—';
  const dt = DateTime.fromISO(iso, { zone: zone || 'America/Toronto' });
  return dt.isValid ? dt.toFormat("ccc LLL d, yyyy 'at' h:mm a ZZZZ") : iso;
}

export default function EmailRemOverlay({ open, onClose, userData }) {
  const entitled = isEmailRemEnabledForUser(userData);
  const [status, setStatus] = useState(null);
  const [aliases, setAliases] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [targets, setTargets] = useState({ flows: [], actions: [] });
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    if (!entitled) return;
    setLoading(true);
    try {
      const [st, aliasList, meetingList, t] = await Promise.all([
        getEmailRemStatus(),
        listEmailRemAliases(),
        listEmailRemMeetings(),
        listEmailRemTargets(),
      ]);
      setStatus(st);
      setAliases(aliasList || []);
      setMeetings(meetingList || []);
      setTargets({ flows: t?.flows || [], actions: t?.actions || [] });
    } catch (e) {
      toast.error(e?.message || 'Failed to load Email Reminders');
    } finally {
      setLoading(false);
    }
  }, [entitled]);

  useEffect(() => {
    if (open && entitled) refresh();
  }, [open, entitled, refresh]);

  const copyAddress = async (address) => {
    try {
      await navigator.clipboard.writeText(address);
      toast.success('Address copied');
    } catch (_) {
      toast.error('Could not copy address');
    }
  };

  const saveAlias = async () => {
    if (!form.label.trim() || !form.targetId) {
      toast.error('Enter a label and choose a Flow or Action.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: form.label.trim(),
        targetType: form.targetType,
        targetId: form.targetId,
        offsetMinutes: Number(form.offsetMinutes),
      };
      if (editingId) {
        await updateEmailRemAlias(editingId, payload);
        toast.success('Destination updated');
        setEditingId(null);
      } else {
        await createEmailRemAlias(payload);
        toast.success('Destination created');
      }
      setForm(emptyForm());
      refresh();
    } catch (e) {
      toast.error(e?.message || (editingId ? 'Failed to update destination' : 'Failed to create destination'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (alias) => {
    setEditingId(alias.id);
    setForm({
      label: alias.label || '',
      targetType: alias.targetType || 'flow',
      targetId: alias.targetId || '',
      offsetMinutes: alias.offsetMinutes || 15,
    });
  };

  const removeAlias = async (alias) => {
    if (!window.confirm('Delete this address? It will stop working and any upcoming reminders for it will be canceled.')) {
      return;
    }
    try {
      await deleteEmailRemAlias(alias.id);
      setAliases((prev) => prev.filter((item) => item.id !== alias.id));
      toast.success('Destination deleted');
      refresh();
    } catch (e) {
      toast.error(e?.message || 'Failed to delete destination');
    }
  };

  const targetOptions = form.targetType === 'action' ? targets.actions : targets.flows;
  const visibleAliases = aliases.filter((a) => !a.deleted && !a.deletedAt);
  const activeCount = visibleAliases.filter((a) => a.active).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      data-testid="email-rem-dialog"
      PaperProps={{ sx: { mx: { xs: 1, sm: 2 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        Email Reminder
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {!entitled ? (
          <Typography>Email Reminders are not enabled for your account. Ask a tenant admin to turn them on.</Typography>
        ) : (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2">
              Forward a calendar invitation to a private address and Signal Bridge will run your selected
              notification before it begins. Recurring meetings schedule the next occurrence only.
            </Typography>
            {status && !status.inboundReady && (
              <Typography variant="body2" color="warning.main" data-testid="email-rem-setup-pending">
                Inbound email setup is not yet active. You can still create destinations; forwarded meetings
                will be processed after SendGrid and DNS are configured.
              </Typography>
            )}
            {status?.accountEmail && (
              <Typography variant="caption" color="text.secondary">
                Only {status.accountEmail} may forward to your addresses.
              </Typography>
            )}

            <Box sx={sectionSx} data-testid="email-rem-create">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {editingId ? 'Edit destination' : 'Create destination'}
              </Typography>
              {activeCount >= 3 && !editingId ? (
                <Typography variant="body2" data-testid="email-rem-cap">
                  You already have three active addresses. Disable one to add another.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  <TextField
                    label="Label"
                    value={form.label}
                    onChange={(e) => setForm({ ...form, label: e.target.value })}
                    fullWidth
                    inputProps={{ 'data-testid': 'email-rem-label' }}
                  />
                  <FormControl fullWidth>
                    <InputLabel id="email-rem-target-type-label">Target</InputLabel>
                    <Select
                      labelId="email-rem-target-type-label"
                      label="Target"
                      value={form.targetType}
                      onChange={(e) => setForm({ ...form, targetType: e.target.value, targetId: '' })}
                      inputProps={{ 'data-testid': 'email-rem-target-type' }}
                    >
                      <MenuItem value="flow">Existing Flow</MenuItem>
                      <MenuItem value="action">Existing Action</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="email-rem-target-id-label">{form.targetType === 'action' ? 'Action' : 'Flow'}</InputLabel>
                    <Select
                      labelId="email-rem-target-id-label"
                      label={form.targetType === 'action' ? 'Action' : 'Flow'}
                      value={form.targetId}
                      onChange={(e) => setForm({ ...form, targetId: e.target.value })}
                      inputProps={{ 'data-testid': 'email-rem-target-id' }}
                    >
                      {targetOptions.map((opt) => (
                        <MenuItem key={opt.id} value={opt.id}>{opt.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel id="email-rem-offset-label">Minutes before</InputLabel>
                    <Select
                      labelId="email-rem-offset-label"
                      label="Minutes before"
                      value={form.offsetMinutes}
                      onChange={(e) => setForm({ ...form, offsetMinutes: e.target.value })}
                      inputProps={{ 'data-testid': 'email-rem-offset' }}
                    >
                      {OFFSETS.map((mins) => (
                        <MenuItem key={mins} value={mins}>{mins} minutes before</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    onClick={saveAlias}
                    disabled={saving}
                    data-testid="email-rem-save"
                  >
                    {editingId ? 'Save changes' : 'Create destination'}
                  </Button>
                  {editingId && (
                    <Button
                      size="small"
                      onClick={() => {
                        setEditingId(null);
                        setForm(emptyForm());
                      }}
                    >
                      Cancel edit
                    </Button>
                  )}
                </Stack>
              )}
            </Box>

            <Box sx={sectionSx} data-testid="email-rem-aliases">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Your addresses</Typography>
              {loading && <Typography variant="body2">Loading…</Typography>}
              {!loading && visibleAliases.length === 0 && (
                <Typography variant="body2" color="text.secondary" data-testid="email-rem-empty">
                  No destinations yet.
                </Typography>
              )}
              {visibleAliases.map((alias) => (
                <Box key={alias.id} sx={{ mb: 1.5 }} data-testid={`email-rem-alias-${alias.id}`}>
                  <Typography fontWeight={600}>{alias.label}</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{alias.address}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {alias.targetType} · {alias.offsetMinutes} min before
                    {alias.active ? '' : ' · disabled'}
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                    <Button size="small" onClick={() => copyAddress(alias.address)} data-testid={`email-rem-copy-${alias.id}`}>
                      Copy address
                    </Button>
                    <Button size="small" onClick={() => startEdit(alias)} data-testid={`email-rem-edit-${alias.id}`}>
                      Edit
                    </Button>
                    <Button
                      size="small"
                      onClick={() => updateEmailRemAlias(alias.id, { active: !alias.active }).then(refresh)}
                      data-testid={`email-rem-disable-${alias.id}`}
                    >
                      {alias.active ? 'Disable' : 'Enable'}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        if (window.confirm('Regenerate this address? The old address stops working immediately.')) {
                          regenerateEmailRemAlias(alias.id).then(refresh);
                        }
                      }}
                      data-testid={`email-rem-regenerate-${alias.id}`}
                    >
                      Regenerate
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => removeAlias(alias)}
                      data-testid={`email-rem-delete-${alias.id}`}
                    >
                      Delete
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Box>

            <Box sx={sectionSx} data-testid="email-rem-meetings">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Upcoming forwarded meetings</Typography>
              {meetings.length === 0 && (
                <Typography variant="body2" color="text.secondary">No forwarded meetings yet.</Typography>
              )}
              {meetings.map((meeting) => (
                <Box key={meeting.id} sx={{ mb: 1.25 }} data-testid={`email-rem-meeting-${meeting.id}`}>
                  <Typography fontWeight={600}>{meeting.title}</Typography>
                  <Typography variant="body2">{formatWhen(meeting.startAt, meeting.timezone)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {meeting.aliasLabel ? `${meeting.aliasLabel} · ` : ''}
                    {meeting.status}
                    {meeting.recurring ? ' · next occurrence only' : ''}
                    {meeting.parseStatus && meeting.parseStatus !== 'ok' ? ` · ${meeting.parseStatus}` : ''}
                  </Typography>
                  {meeting.status === 'scheduled' && (
                    <Button size="small" sx={{ ml: 0 }} onClick={() => cancelEmailRemMeeting(meeting.id).then(refresh)}>
                      Cancel reminder
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
